import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, MailCheck, RefreshCw, CheckCircle2, Send } from 'lucide-react';
import { api, ErrorApi } from '../api';
import { useAuth } from '../lib/contextoAuth';
import {
  MarcoPuerta, PanelVisual, TexturaPuntos, CampoPuerta, BotonPuerta,
  AvisoError, Plegado,
} from '../publico/puerta';
import { CLASE_CAMPO } from '../publico/medidas';
import CodigoQR from '../ui/CodigoQR';

/* =====================================================================
   CONFIRMA TU CUENTA · 30 de agosto de 2026 · dos caminos desde el 19 de septiembre
   =====================================================================
   La única pantalla que ve una cuenta recién creada hasta que confirma.
   Todo lo demás de su empresa está cerrado con `CORREO_SIN_VERIFICAR`,
   así que ESTA PANTALLA TIENE QUE BASTARSE SOLA: si aquí alguien se
   atora, no tiene a dónde ir.

   TIENE DOS CARAS, Y LA ELIGE EL SERVIDOR con `salida_real`:

     · SIN SALIDA DE CORREO (`salida_real: false`): el único camino es
       Telegram, y se ofrece como lo que es: el camino principal, con el
       botón grande. NO SE MENCIONA EL CORREO — ni «revisa tu bandeja»,
       ni «no te va a llegar», ni de dónde sacar el enlace. Quien lee es
       un cliente, y nada de cómo está montado el servidor le sirve.
     · CON SALIDA DE CORREO (`salida_real: true`): «te mandamos un correo
       a X; si no llega en dos minutos, confirma por Telegram». El correo
       es el camino normal y Telegram la salida de emergencia, con el
       botón de reenviar y la lista de qué hacer si no llega.

   POR QUÉ CAMBIÓ EL 19 DE SEPTIEMBRE DE 2026. La primera clienta real
   llegó aquí y leyó «la salida de correo de este servidor no está
   configurada… el enlace queda en la bitácora… quien lo instaló puede
   sacarlo». Era verdad y era inútil: texto de dentro en una pantalla de
   fuera. La regla del dueño desde ese día: el enlace de confirmación le
   llega al solicitante —por Telegram y, cuando hay salida, por correo—
   y a nadie más. Esta pantalla ya no habla de servidores ni bitácoras.

   Lo que sigue contestando sin que nadie pregunte:

     1. ¿Cuál es mi cuenta?          → la dirección, en grande
     2. ¿Cómo confirmo?               → Telegram (siempre) y correo (si sale)
     3. ¿Y si escribí mal el correo?  → «me equivoqué de correo»
     4. ¿Y si el correo no llega?     → spam, reenviar, y Telegram

   LA TERCERA CARA · R-SD8 (29 sep 2026). SIN TELEGRAM (`confirmar_por_correo`
   verdadero y sin salida real) la pantalla decía «se hace por Telegram» y
   el bloque de Telegram contestaba «por ahora no está disponible»: la
   empresa quedaba creada y cerrada, sin ninguna salida. Ahora ofrece el
   enlace que el alta ya mandó por correo (`/app?ir=verificar&t=`), con el
   botón de reenviar, y no enseña un bloque de Telegram que no lleva a
   ningún sitio.
   ===================================================================== */

// Cada cuánto baja el número del botón. Un segundo, porque una cuenta
// atrás que salta de diez en diez parece congelada.
const TIC = 1000;

export default function ConfirmaTuCorreo() {
  const { cerrarSesion, marcarCorreoConfirmado } = useAuth();
  const [estado, setEstado] = useState(null);
  const [faltan, setFaltan] = useState(0);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [corrigiendo, setCorrigiendo] = useState(false);
  const [nuevoCorreo, setNuevoCorreo] = useState('');
  const [listo, setListo] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const d = await api.get('/verificacion/estado');
      setEstado(d);
      setFaltan(d.segundos_para_reenviar || 0);
      if (d.verificado) { setListo(true); marcarCorreoConfirmado(); }
    } catch (e) {
      setError(e.message);
    }
  }, [marcarCorreoConfirmado]);

  useEffect(() => { cargar(); }, [cargar]);

  // LA CUENTA ATRÁS DEL BOTÓN. Sin ella el botón está apagado sin decir
  // por qué, que es exactamente lo que la doctrina prohíbe: «botones
  // deshabilitados con la explicación visible».
  useEffect(() => {
    if (faltan <= 0) return undefined;
    const t = setTimeout(() => setFaltan((n) => n - 1), TIC);
    return () => clearTimeout(t);
  }, [faltan]);

  // SI YA CONFIRMÓ EN OTRA PESTAÑA, ESTA SE ENTERA SOLA.
  //
  // El enlace se abre casi siempre en el teléfono, y la pestaña de la
  // computadora se queda aquí esperando. Sin esto, la persona confirma,
  // vuelve a la computadora y sigue viendo «confirma tu correo»: parece
  // que no funcionó, y pide otro correo. Se pregunta cada diez segundos,
  // que es barato y suficiente.
  const vivo = useRef(true);
  useEffect(() => {
    vivo.current = true;
    const t = setInterval(() => { if (vivo.current && !listo) cargar(); }, 10000);
    return () => { vivo.current = false; clearInterval(t); };
  }, [cargar, listo]);

  async function reenviar() {
    setOcupado(true); setError(''); setAviso('');
    try {
      const d = await api.post('/verificacion/reenviar', {});
      setAviso(d.mensaje);
      setFaltan(d.segundos_para_reenviar || 60);
      await cargar();
    } catch (e) {
      setError(e.message);
      // UN 429 NO ES UN FALLO QUE HAYA QUE REINTENTAR: es el minuto de
      // cortesía. Se reaprovecha para volver a arrancar la cuenta atrás,
      // así el botón no queda encendido invitando a chocar otra vez.
      if (e instanceof ErrorApi && e.status === 429) await cargar();
    } finally {
      setOcupado(false);
    }
  }

  async function corregir(ev) {
    ev.preventDefault();
    setOcupado(true); setError(''); setAviso('');
    try {
      const d = await api.post('/verificacion/corregir', { email: nuevoCorreo });
      setAviso(d.mensaje);
      setCorrigiendo(false);
      setNuevoCorreo('');
      await cargar();
    } catch (e) {
      setError(e.message);
    } finally {
      setOcupado(false);
    }
  }

  if (listo) return <YaQuedo />;

  const correo = estado?.correo || '';
  const salidaReal = estado?.salida_real === true;
  // R-SD8: sin Telegram, la vía es el enlace del correo.
  const sinTelegram = !salidaReal && estado?.confirmar_por_correo === true;
  const porCorreo = salidaReal || sinTelegram;
  // MIENTRAS NO SE SABE QUÉ CARA PINTAR, no se pinta ninguna a medias:
  // enseñar la del correo y cambiarla a la de Telegram medio segundo
  // después es la clase de parpadeo que hace dudar de la pantalla.
  if (!estado && !error) {
    return (
      <MarcoPuerta panel={<PanelDeMarca />}>
        <div className="space-y-4 text-center">
          <Loader2 size={28} className="animate-spin mx-auto text-[var(--p-marca)]"
                   aria-hidden="true" />
          <p className="text-[0.9375rem] text-[var(--p-tinta-2)]">Un momento…</p>
        </div>
      </MarcoPuerta>
    );
  }

  return (
    <MarcoPuerta panel={<PanelDeMarca />}>
      <div className="space-y-6">
        <div>
          <div className="inline-flex items-center justify-center w-11 h-11 rounded-full
                          bg-[var(--p-marca-tenue)] text-[var(--p-marca)] mb-4">
            {porCorreo
              ? <MailCheck size={22} aria-hidden="true" />
              : <Send size={22} aria-hidden="true" />}
          </div>
          <h1 className="text-[1.625rem] font-bold tracking-tight leading-tight">
            {porCorreo ? 'Confirma tu correo' : 'Confirma tu cuenta'}
          </h1>
          {/* EL SUBTÍTULO DICE PARA QUÉ SIRVE LA PANTALLA, criterio 2. Y
              dice el camino: con salida de correo, el correo primero y
              Telegram si no llega; sin ella, Telegram y nada más. */}
          <p className="mt-2 text-[0.9375rem] leading-relaxed text-[var(--p-tinta-2)]">
            {salidaReal ? (
              <>
                Tu empresa ya está creada. Te enviamos un correo a{' '}
                <strong className="font-semibold text-[var(--p-tinta)]">{correo}</strong>:
                abre el enlace que trae y tu cuenta queda lista. Si no llega
                en dos minutos, confirma por Telegram aquí abajo.
              </>
            ) : sinTelegram ? (
              <>
                Tu empresa ya está creada. Para comprobar que eres tú, abre
                el enlace que mandamos a{' '}
                <strong className="font-semibold text-[var(--p-tinta)]">{correo}</strong>:
                tu cuenta queda lista en ese momento.
              </>
            ) : (
              <>
                Tu empresa ya está creada. Falta comprobar que eres tú:
                se hace por Telegram y tarda diez segundos.
              </>
            )}
          </p>
        </div>

        {/* LA DIRECCIÓN, EN GRANDE. Es el dato con el que la persona
            descubre la errata: verla en letra chica no sirve de nada. */}
        <div className="rounded border border-[var(--p-linea)] bg-[var(--p-fondo-2)] p-4">
          <p className="text-[0.8125rem] text-[var(--p-tinta-3)]">
            {salidaReal ? 'Lo mandamos a' : sinTelegram ? 'El enlace va a' : 'Tu cuenta'}
          </p>
          <p className="mt-1 text-[1.0625rem] font-semibold break-all">{correo}</p>
        </div>

        {aviso && (
          <p role="status"
             className="text-sm leading-relaxed text-[var(--p-tinta-2)]
                        bg-[var(--p-fondo-2)] border border-[var(--p-linea)] rounded p-3">
            {aviso}
          </p>
        )}
        <AvisoError>{error}</AvisoError>

        {/* SIN SALIDA DE CORREO, TELEGRAM ES EL BOTÓN GRANDE. Con salida,
            va después del reenvío, como la salida de emergencia. */}
        {!salidaReal && !sinTelegram && (
          <ConfirmarPorTelegram principal
                                disponible={estado?.telegram_disponible !== false} />
        )}

        {/* SIN TELEGRAM, EL ENLACE DEL CORREO ES EL CAMINO (R-SD8). El
            botón de reenviar con su cuenta atrás, y qué hacer si no se ve. */}
        {sinTelegram && (
          <div className="space-y-3">
            <BotonPuerta type="button" onClick={reenviar}
                         apagado={ocupado || faltan > 0}
                         disabled={ocupado || faltan > 0}>
              {ocupado
                ? <><Loader2 size={18} className="animate-spin" aria-hidden="true" /> Mandando…</>
                : <><RefreshCw size={18} aria-hidden="true" /> Mandar el enlace a mi correo</>}
            </BotonPuerta>
            {faltan > 0 && (
              <p className="text-[0.8125rem] text-center text-[var(--p-tinta-3)]">
                Podrás pedir otro en {faltan} segundo{faltan === 1 ? '' : 's'}.
              </p>
            )}
            <Plegado resumen="No veo el enlace, ¿qué hago?">
              <ul className="mt-2 space-y-2 text-[0.8125rem] leading-relaxed text-[var(--p-tinta-2)] list-disc pl-4">
                <li>
                  Busca en <strong>correo no deseado</strong> o «spam».
                </li>
                <li>
                  Revisa que la dirección de arriba esté bien escrita. Si no,
                  usa «Me equivoqué de correo».
                </li>
                <li>
                  Pide otro con el botón; el anterior deja de servir y solo
                  vale el último.
                </li>
              </ul>
            </Plegado>
          </div>
        )}

        {salidaReal && (
          <div className="space-y-3">
            <BotonPuerta type="button" onClick={reenviar}
                         apagado={ocupado || faltan > 0}
                         disabled={ocupado || faltan > 0}>
              {ocupado
                ? <><Loader2 size={18} className="animate-spin" aria-hidden="true" /> Mandando…</>
                : <><RefreshCw size={18} aria-hidden="true" /> Mandar el enlace otra vez</>}
            </BotonPuerta>
            {/* POR QUÉ ESTÁ APAGADO, VISIBLE. Criterio 8. */}
            {faltan > 0 && (
              <p className="text-[0.8125rem] text-center text-[var(--p-tinta-3)]">
                Podrás pedir otro en {faltan} segundo{faltan === 1 ? '' : 's'}.
              </p>
            )}
          </div>
        )}

        {salidaReal && (
          <ConfirmarPorTelegram disponible={estado?.telegram_disponible !== false} />
        )}

        {/* ME EQUIVOQUÉ DE CORREO. La salida de la trampa: sin esto, una
            letra mal tecleada deja la cuenta muerta para siempre. Con el
            correo como identidad importa igual en las dos caras: es con
            lo que va a entrar mañana. */}
        {!corrigiendo ? (
          <button type="button" onClick={() => setCorrigiendo(true)}
                  className="w-full text-[0.875rem] font-semibold text-[var(--p-marca)] hover:underline">
            Me equivoqué de correo
          </button>
        ) : (
          <form onSubmit={corregir} className="space-y-3">
            <CampoPuerta
              id="correo-nuevo"
              etiqueta="Tu correo, bien escrito"
              ayuda={salidaReal
                ? 'Cambiamos la dirección de tu cuenta y mandamos el enlace ahí. Con esta dirección entrarás a partir de ahora.'
                : 'Cambiamos la dirección de tu cuenta. Con esta dirección entrarás a partir de ahora.'}
            >
              <input id="correo-nuevo" type="email" required autoFocus
                     className={CLASE_CAMPO} value={nuevoCorreo}
                     onChange={(e) => setNuevoCorreo(e.target.value)}
                     placeholder="tunombre@tuempresa.mx" />
            </CampoPuerta>
            <div className="flex gap-2">
              <BotonPuerta apagado={ocupado} disabled={ocupado}>
                {ocupado
                  ? <><Loader2 size={18} className="animate-spin" aria-hidden="true" /> Guardando…</>
                  : salidaReal ? 'Guardar y mandar el enlace' : 'Guardar'}
              </BotonPuerta>
            </div>
            <button type="button" onClick={() => { setCorrigiendo(false); setError(''); }}
                    className="w-full text-[0.8125rem] text-[var(--p-tinta-3)] hover:underline">
              Cancelar
            </button>
          </form>
        )}

        {salidaReal && (
          <Plegado resumen="No me llega el correo, ¿qué hago?">
            <ul className="mt-2 space-y-2 text-[0.8125rem] leading-relaxed text-[var(--p-tinta-2)] list-disc pl-4">
              <li>
                Busca en <strong>correo no deseado</strong> o «spam».
              </li>
              <li>
                Revisa que la dirección de arriba esté bien escrita. Si no,
                usa «Me equivoqué de correo».
              </li>
              <li>
                Puede tardar un par de minutos. Pide otro con el botón; el
                anterior deja de servir y solo vale el último.
              </li>
              <li>
                Confirma por Telegram: es igual de seguro y no depende del
                correo.
              </li>
              <li>
                Si tu correo es de la empresa, puede que el filtro de tu empresa lo esté
                bloqueando. Prueba con una dirección personal y luego la
                cambias desde Usuarios.
              </li>
            </ul>
          </Plegado>
        )}

        <div className="pt-2 border-t border-[var(--p-linea)] flex items-center justify-between">
          <a href="/" className="text-[0.8125rem] text-[var(--p-tinta-3)] hover:underline">
            Volver al sitio
          </a>
          <button type="button" onClick={cerrarSesion}
                  className="text-[0.8125rem] text-[var(--p-tinta-3)] hover:underline">
            Cerrar sesión
          </button>
        </div>
      </div>
    </MarcoPuerta>
  );
}

/* --- CONFIRMAR POR TELEGRAM -----------------------------------------
   LA PUERTA QUE SIEMPRE ESTÁ, y desde el 19 de septiembre de 2026 la
   PRINCIPAL cuando el servidor no manda correo a una persona.

   El bot de Telegram sale a internet —es él quien llama, no al revés—,
   así que confirma igual de bien que el correo y en cualquier instalación.
   El correo NO se quita: sigue siendo la identidad, y es el camino
   normal en cuanto hay salida real.

   CUATRO DECISIONES DE ESTE BLOQUE:

   1. NO SE PIDE EL ENLACE SOLO AL ABRIR. Pedirlo invalida el del correo
      —solo puede haber uno vivo— así que si se pidiera de entrada, abrir
      esta pantalla rompería el enlace que la persona quizá ya tiene
      abierto en otra pestaña. Se pide cuando lo pulsa.
   2. SE DICE QUE EL ANTERIOR DEJA DE VALER, antes de pulsarlo y no
      después — pero SOLO cuando hubo un correo: sin salida de correo no
      hay nada que invalidar y decirlo sería hablar de un correo que no
      existe.
   3. EL QR VA JUNTO AL BOTÓN, no en su lugar. En el teléfono el botón es
      lo que sirve —abre Telegram directo—; el QR es para quien está en
      la computadora y tiene Telegram en el teléfono.
   4. `principal` cambia el TAMAÑO, no el mecanismo: el mismo enlace, el
      mismo token de un solo uso ligado a esta cuenta, el mismo bot.
   -------------------------------------------------------------------- */
function ConfirmarPorTelegram({ principal = false, disponible = true }) {
  const [enlace, setEnlace] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [fallo, setFallo] = useState('');

  async function pedir() {
    setCargando(true);
    setFallo('');
    try {
      const d = await api.post('/verificacion/enlace-telegram', {});
      setEnlace(d);
    } catch (e) {
      setFallo(e.message);
    } finally {
      setCargando(false);
    }
  }

  // SIN BOT NO HAY BOTÓN QUE NO LLEVE A NADA. Se dice qué pasa y qué
  // hacer, en neutro, sin nombrar cómo está montado el servidor.
  if (!disponible) {
    return (
      <div className="rounded border border-[var(--p-linea)] p-4 space-y-2">
        <p className="text-[0.9375rem] font-semibold">Confirmar por Telegram</p>
        <p className="text-[0.8125rem] leading-relaxed text-[var(--p-tinta-2)]">
          No está disponible en este momento. Vuelve a intentarlo en unos minutos;
          esta pantalla se actualiza sola.
        </p>
      </div>
    );
  }

  if (enlace) {
    return (
      <div className="rounded border border-[var(--p-linea)] bg-[var(--p-fondo-2)] p-4 space-y-3">
        <p className="text-[0.9375rem] font-semibold">
          Abre este enlace en Telegram
        </p>
        <a
          href={enlace.enlace}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full h-12 rounded
                     font-semibold text-white bg-[var(--p-marca-solido)]"
        >
          <Send size={18} aria-hidden="true" />
          Abrir @{enlace.bot} en Telegram
        </a>

        <div className="flex flex-col items-center gap-2 pt-1">
          <CodigoQR
            valor={enlace.enlace}
            tamano={148}
            etiqueta="Código para confirmar tu cuenta por Telegram"
          />
          <p className="text-[0.8125rem] text-center text-[var(--p-tinta-3)] leading-relaxed">
            ¿Estás en la computadora? Apunta la cámara del teléfono a este
            código y se abre solo.
          </p>
        </div>

        <p className="text-[0.8125rem] leading-relaxed text-[var(--p-tinta-2)]">
          En el chat, pulsa <strong>Iniciar</strong>. Tu cuenta queda
          confirmada en ese momento y esta pantalla se actualiza sola. A
          partir de ahí el bot también puede avisarte de lo importante; si
          no quieres, le escribes <code>/avisos off</code>.
        </p>
      </div>
    );
  }

  const boton = cargando ? (
    <><Loader2 size={18} className="animate-spin" aria-hidden="true" /> Preparando…</>
  ) : (
    <><Send size={18} aria-hidden="true" /> Confirmar por Telegram</>
  );

  if (principal) {
    return (
      <div className="space-y-3">
        {fallo && <AvisoError>{fallo}</AvisoError>}
        <BotonPuerta type="button" onClick={pedir} apagado={cargando} disabled={cargando}>
          {boton}
        </BotonPuerta>
        <p className="text-[0.8125rem] text-center leading-relaxed text-[var(--p-tinta-3)]">
          Se abre el chat con nuestro bot. Pulsas «Iniciar» y listo: tarda
          diez segundos.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded border border-[var(--p-linea)] p-4 space-y-2">
      <p className="text-[0.9375rem] font-semibold">
        ¿No te llega el correo? Confirma por Telegram
      </p>
      <p className="text-[0.8125rem] leading-relaxed text-[var(--p-tinta-2)]">
        Es igual de seguro y tarda diez segundos. Te damos un enlace que
        abre nuestro bot y listo.{' '}
        <strong>Al pedirlo, el enlace del correo deja de servir</strong> y
        solo vale este.
      </p>
      {fallo && <AvisoError>{fallo}</AvisoError>}
      <button
        type="button"
        onClick={pedir}
        disabled={cargando}
        className="w-full h-12 rounded font-semibold border
                   border-[var(--p-marca)] text-[var(--p-marca)]
                   disabled:opacity-60 flex items-center justify-center gap-2"
      >
        {boton}
      </button>
    </div>
  );
}

/* --- CONFIRMADO ------------------------------------------------------
   Pantalla propia y no un mensaje verde arriba: confirmar es el final de
   un trámite, y merece decirlo entero. Criterio 7. */
function YaQuedo() {
  const { autenticado, marcarCorreoConfirmado } = useAuth();
  // SI EL ENLACE SE ABRIÓ EN LA MISMA PESTAÑA DONDE HAY SESIÓN, la
  // consola se abre sola: el candado ya no aplica y hacer que la persona
  // pulse otro botón para llegar donde ya puede llegar es un paso de
  // más. Si no hay sesión —el caso normal, el teléfono— el botón lleva a
  // la pantalla de acceso, que es lo que toca.
  useEffect(() => { if (autenticado) marcarCorreoConfirmado(); },
    [autenticado, marcarCorreoConfirmado]);

  return (
    <MarcoPuerta panel={<PanelDeMarca />}>
      <div className="space-y-6 text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full
                        bg-[var(--p-bien-flojo)] text-[var(--p-bien)]">
          <CheckCircle2 size={30} aria-hidden="true" />
        </div>
        <div>
          <h1 className="text-[1.625rem] font-bold tracking-tight">
            Listo, tu cuenta quedó confirmada
          </h1>
          <p className="mt-2 text-[0.9375rem] leading-relaxed text-[var(--p-tinta-2)]">
            Ya puedes usar TEGO completo. El primer paso es
            dar de alta tus productos — TEGO te va a guiar.
          </p>
        </div>
        <BotonPuerta type="button"
                     onClick={() => window.location.assign(
                       autenticado ? '/app' : '/app?ir=login')}>
          {autenticado ? 'Entrar a TEGO' : 'Entrar con tu correo y contraseña'}
        </BotonPuerta>
      </div>
    </MarcoPuerta>
  );
}

/* =====================================================================
   CANJEAR EL ENLACE · pantalla pública
   =====================================================================
   Quien abre el enlace del correo casi siempre lo hace en el teléfono,
   donde no ha iniciado sesión. Por eso esto NO pide sesión: lo que lo
   protege es el token.
   ===================================================================== */
export function CanjeandoElEnlace({ token }) {
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let vivo = true;
    api.post('/verificacion/confirmar', { token })
      .then((d) => { if (vivo) setResultado(d); })
      .catch((e) => { if (vivo) setError(e.message); });
    return () => { vivo = false; };
  }, [token]);

  if (resultado) return <YaQuedo />;

  if (error) {
    return (
      <MarcoPuerta panel={<PanelDeMarca />}>
        <div className="space-y-5">
          <h1 className="text-[1.5rem] font-bold tracking-tight">
            Ese enlace ya no sirve
          </h1>
          <AvisoError>{error}</AvisoError>
          {/* QUÉ HACER AHORA, que es la tercera cosa que todo error debe
              decir. Un enlace muerto sin salida es un callejón. */}
          <p className="text-[0.9375rem] leading-relaxed text-[var(--p-tinta-2)]">
            No pasa nada y no perdiste tu empresa: entra con tu correo y tu
            contraseña, y desde la pantalla de confirmación pides un enlace
            nuevo con un botón.
          </p>
          <BotonPuerta type="button"
                       onClick={() => window.location.assign('/app?ir=login')}>
            Entrar y pedir otro enlace
          </BotonPuerta>
        </div>
      </MarcoPuerta>
    );
  }

  // MIENTRAS TANTO, ALGO QUE MIRAR. Criterio 11: nunca una pantalla
  // congelada sin explicación.
  return (
    <MarcoPuerta panel={<PanelDeMarca />}>
      <div className="space-y-4 text-center">
        <Loader2 size={28} className="animate-spin mx-auto text-[var(--p-marca)]"
                 aria-hidden="true" />
        <p className="text-[0.9375rem] text-[var(--p-tinta-2)]">
          Confirmando tu correo…
        </p>
      </div>
    </MarcoPuerta>
  );
}

function PanelDeMarca() {
  return (
    <PanelVisual
      fondo="linear-gradient(152deg, #0e5397 0%, #08355f 32%, #061e36 60%, #040c15 100%)"
      halo={
        'radial-gradient(46rem 32rem at 4% 96%, rgba(219,146,60,0.30), transparent 64%),'
        + 'radial-gradient(40rem 30rem at 98% -8%, rgba(77,163,240,0.22), transparent 66%)'
      }
      textura={<TexturaPuntos id="confirmar" />}
      marca={
        <span className="text-2xl font-bold tracking-tight">
          TEGO<span className="text-[#7cbdf7]">Cloud</span>
        </span>
      }
      frase="Un paso más y tu bodega está en línea."
      pie={
        <span className="text-[0.8125rem] font-medium tracking-wide text-[var(--p-sobre-noche-2)]">
          Inventario para bodegas y tiendas · México
        </span>
      }
    />
  );
}
